import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import TripStoryCard from "../components/app/TripStoryCard";
import MagicLinkForm from "../components/app/MagicLinkForm";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Camera, ArrowRight, CheckCircle, Loader2 } from "lucide-react";

/**
 * ShareYourTrip — iter210
 *
 * Public, SEO-indexed deep-link landing at `/share-your-trip`. The whole
 * point is that any external post / reel / QR code can link straight here
 * and either:
 *   1. Signed-in visitor → auto-redirect to /wall?new=1 (composer opens).
 *   2. Anonymous visitor → sees a live grid of recent trip photos plus a
 *      1-click passwordless sign-up (Magic Link). The magic link finishes
 *      at /magic which honours the `?next=` param, so after the tap they
 *      land on /wall?new=1 with the composer already open.
 *
 * Zero-work SEO: this page contains real UGC (photos + captions from the
 * public Trip Wall) so Google indexes it with fresh, unique content every
 * time it re-crawls. It also carries `HowTo` structured data so it can
 * show up as a Google rich result for "how to share camping trip photos"
 * / "post my UK campsite trip" queries.
 */
const NEXT = "/wall?new=1";
const NEXT_ENCODED = encodeURIComponent(NEXT);

export default function ShareYourTrip() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Signed-in users skip the landing entirely and go straight to the composer.
  useEffect(() => {
    if (user) nav("/wall?new=1", { replace: true });
  }, [user, nav]);

  // Load a fresh sample of the wall to show visitors what they're joining.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/wall", { params: { limit: 9 } });
        setStories(data?.items || []);
        setTotal(data?.total || 0);
      } catch {
        setStories([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Structured data (HowTo) — free eligibility for a Google rich result.
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "Share your UK camping trip on CampCompass",
      description:
        "Post a photo + honest one-liner about a UK campsite you've stayed at. Free, no card, no algorithm — real trips only.",
      totalTime: "PT2M",
      step: [
        {
          "@type": "HowToStep",
          position: 1,
          name: "Get a free CampCompass account",
          text: "Tap the button, pop in your email — we email you a 1-click sign-in link. No password.",
        },
        {
          "@type": "HowToStep",
          position: 2,
          name: "Pick the campsite you stayed at",
          text: "Search 22,000+ UK campsites. Tag the one you visited so other campers see your review under it.",
        },
        {
          "@type": "HowToStep",
          position: 3,
          name: "Add a photo + one line",
          text: "Drop a picture from the trip, a rating, and one honest sentence. Done in under 2 minutes.",
        },
      ],
    }),
    []
  );

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="share-your-trip-page">
      <Header />

      {/* JSON-LD for SEO rich results */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b hairline-strong bg-gradient-to-br from-[#ff4500] via-[#e03d00] to-[#a82e00] text-white">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <span className="eyebrow !text-white/80" data-testid="syt-eyebrow">
              The Trip Wall · Free · No card
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 mb-4">
              Share your UK camping trip.
              <br />
              <span className="italic font-light opacity-90">Get an account in 20 seconds.</span>
            </h1>
            <p className="text-white/90 text-base lg:text-lg max-w-xl leading-relaxed">
              Pop your email in. We send you a 1-click sign-in link (no password to invent, no card
              to type). Tap it, and you're straight in to post a photo + one honest line about a
              campsite you've actually stayed at.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/90">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> Real trips only — no bots, no
                affiliate spin
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> {total > 0 ? `${total.toLocaleString()}+ ` : ""}campers already sharing
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> Free while we grow — no credit
                card
              </li>
            </ul>
          </div>

          {/* SIGN-UP CARD */}
          <div className="lg:justify-self-end w-full lg:max-w-sm">
            <div className="bg-white text-[#0e0e0e] p-1 shadow-[0_10px_0_-2px_rgba(0,0,0,0.15)]">
              <div className="border hairline-strong p-6">
                <div className="eyebrow mb-2 flex items-center gap-1.5">
                  <Camera className="w-3.5 h-3.5" /> Sign up + post in one flow
                </div>
                <h2 className="font-display text-2xl leading-tight mb-4">
                  Get in, share, done.
                </h2>
                <MagicLinkForm testId="syt-magic" />
                <div className="mt-4 text-[11px] text-[#666] leading-snug">
                  Prefer a password?{" "}
                  <Link
                    to={`/register?next=${NEXT_ENCODED}`}
                    className="underline text-[#ff4500]"
                    data-testid="syt-to-register"
                  >
                    Create account
                  </Link>{" "}
                  ·{" "}
                  <Link
                    to={`/login?next=${NEXT_ENCODED}`}
                    className="underline text-[#ff4500]"
                    data-testid="syt-to-login"
                  >
                    Log in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE WALL PREVIEW — real content for SEO + social proof */}
      <section className="border-b hairline max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="eyebrow mb-1.5">What campers are posting right now</div>
            <h2 className="font-display text-2xl md:text-3xl">Real trips, real photos, real sites</h2>
          </div>
          <Link
            to="/wall"
            className="text-xs mono uppercase tracking-widest underline"
            data-testid="syt-see-full-wall"
          >
            See the full wall <ArrowRight className="inline w-3 h-3 -mt-0.5" />
          </Link>
        </div>

        {loading ? (
          <div className="py-16 text-center text-[#888]">
            <Loader2 className="w-6 h-6 animate-spin inline-block" />
          </div>
        ) : stories.length === 0 ? (
          <div className="py-16 text-center text-[#888]">
            No trips yet — be the first to post yours.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {stories.slice(0, 9).map((s) => (
              <TripStoryCard key={s.story_id} story={s} onChange={() => {}} onDelete={() => {}} />
            ))}
          </div>
        )}
      </section>

      {/* FAQ — indexable long-form text; picks up "how to share camping trip" queries */}
      <section className="max-w-3xl mx-auto w-full px-6 lg:px-10 py-12 lg:py-16">
        <h2 className="font-display text-2xl md:text-3xl mb-6">Common questions</h2>
        <div className="space-y-4 text-[15px] leading-relaxed">
          <details className="border hairline p-4">
            <summary className="font-bold cursor-pointer">Is it really free?</summary>
            <p className="mt-2 text-[#444]">
              Yes — free while we grow, no credit card, no trial-ending countdown. If we ever add
              paid features later, everything you post stays free forever.
            </p>
          </details>
          <details className="border hairline p-4">
            <summary className="font-bold cursor-pointer">Do I need to write a review?</summary>
            <p className="mt-2 text-[#444]">
              Nope. One photo and one honest line is fine. Long reviews are welcome, but a single
              picture from your pitch + a sentence is what most campers post.
            </p>
          </details>
          <details className="border hairline p-4">
            <summary className="font-bold cursor-pointer">What is a Magic Link?</summary>
            <p className="mt-2 text-[#444]">
              A single-use, 1-click sign-in link we email you. Tap it, you're in. No password to
              invent, remember, or reset. Expires in 15 minutes.
            </p>
          </details>
          <details className="border hairline p-4">
            <summary className="font-bold cursor-pointer">Will my photos be used to sell things?</summary>
            <p className="mt-2 text-[#444]">
              Never. CampCompass has no ads, no affiliate spin on the Trip Wall, and no
              engagement-driven algorithm. Your photo appears on the campsite you tagged and in
              the community feed. That's it.
            </p>
          </details>
        </div>

        <div className="mt-10 p-6 border-2 border-[#0e0e0e] bg-[#fff8f4]">
          <h3 className="font-display text-xl mb-2">Ready to post yours?</h3>
          <p className="text-sm text-[#555] mb-4">
            Drop your email and we'll email you a 1-tap sign-in link. Straight to the composer.
          </p>
          <MagicLinkForm testId="syt-magic-bottom" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
